import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { FormControlLabel, Switch } from '@mui/material';
import i18n from 'i18next';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import Dropdown from '@/components/UI/Dropdown/Dropdown';
import Spinner from '@/components/UI/Spinner/Spinner';
import { allExtensionsFile, ADMIN_CR, ATTACHMENT_SECTION, ATTACHMENT_TAGS, CAMPAIGN_MANAGER_CR, COMMITTEE_COORDINATOR_CR, CREATOR_CR, HELP_AREA, statusCampaign, JV_VISIBLE,
  VAN_CAMPAIGN_MANAGER_CR, VAN_COMMITTEE_COORDINATOR_CR } from '@/helpers/constants';
import { CR_PERMISSION_ACTIONS } from '@/helpers/crPermissions';
import { splitFileName, prepareFiles as prepareFilesUtil, removePercentageFromName } from '@/helpers/fileUtils';
import useCheckDisabled from '@/hooks/useCheckDisabled';
import useCRPermissionMatrix from '@/hooks/useCRPermissionMatrix';
import useDownloadFile from '@/hooks/useDownloadFile';
import useError from '@/hooks/useError';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import { addCampaignAttachments, setCampaignAttachments, deleteCampaignAttachment, toggleAttachmentFavorite, updateAttachmentField as updateAttachmentFieldAction, addAfterQmbAttachments } from '@/redux/Campaign/campaign.action';
import { getCampaignAttachments, getCampaignInfo } from '@/redux/Campaign/campaign.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import campaignServices from '@/services/campaignServices';
import documentationServices from '@/services/documentationServices';
import { Attachment, AttachmentsProps, AttachmentsResponse, CampaignAttachments, PendingUpload } from '@/types';
import AttachmentCollection from './AttachmentCollection/AttachmentCollection';
import styles from './AttachmentCollection/AttachmentCollection.module.scss';

const AFTER_QMB_STATUSES = [
  statusCampaign.IN_PLANT_IMPLEMENTATION,
  statusCampaign.IN_VAN_PLANT_IMPLEMENTATION,
  statusCampaign.IN_QMB_VAN_INFORMATION,
  statusCampaign.COMPLETED,
  statusCampaign.CLOSED,
] as string[];

const Attachments = (props: AttachmentsProps) => {
  const { t } = useTranslation();
  const {
    campaignAttachments, campaignInfo, setHelpArea,
    setAttachments, addAttachments, deleteAttachment, toggleFavorite,
    updateField, addAfterQmb, subCR, userSettings, reloadCR,
  } = props;

  const { id, version } = useParams<{ id: string; version?: string }>();
  const { isButtonDisabled } = useCRPermissionMatrix(campaignInfo?.status, campaignInfo?.authorization);
  const { setLoading: setGlobalLoading } = useGlobalSpinnerContext();
  const { addToast } = useToastContext();
  const { handleCriticalError } = useError();
  const { downloadFile } = useDownloadFile();
  const checkDisabled = useCheckDisabled(false) || subCR;

  const isInPlantImplementation = campaignInfo.status === statusCampaign.IN_PLANT_IMPLEMENTATION || campaignInfo.status === statusCampaign.IN_VAN_PLANT_IMPLEMENTATION;
  const canUpload = !isButtonDisabled(CR_PERMISSION_ACTIONS.UPLOAD_ATTACHMENT);
  const isCreatorOrAdmin = [ADMIN_CR, CREATOR_CR].some(
    (role) => role === campaignInfo?.authorization?.kindOfPermission || role === campaignInfo?.authorization?.campaignRole,
  );
  const canEditBeforeQmb = isCreatorOrAdmin && campaignInfo?.status === statusCampaign.IN_CREATION;
  const isAfterQMBSection = AFTER_QMB_STATUSES.includes(campaignInfo.status);
  const canDeleteAfterQmb = isInPlantImplementation
    && [ADMIN_CR, CAMPAIGN_MANAGER_CR, COMMITTEE_COORDINATOR_CR, VAN_CAMPAIGN_MANAGER_CR, VAN_COMMITTEE_COORDINATOR_CR].some(
      (role) => role === campaignInfo?.authorization?.kindOfPermission || role === campaignInfo?.authorization?.campaignRole,
    );

  const [isLoading, setIsLoading] = useState(true);
  const [deleteFileId, setDeleteFileId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number> | null>(null);
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null);

  const subCampaignAtts = campaignAttachments?.subCampaignAttachments;
  const beforeAttachments = useMemo(() => ({
    attachments: campaignAttachments?.attachments || [],
  }), [campaignAttachments]);
  const afterAttachments = useMemo(() => ({
    attachments: subCampaignAtts?.attachments || [],
  }), [subCampaignAtts]);

  const isInSubCampaign = (attachmentId: string) =>
    afterAttachments.attachments.some((a) => a.id === attachmentId);

  const handleServiceError = (error: { message?: string }) => {
    addToast(error.message, 'ERROR');
    getAttachments();
  };

  const parseSubCRResponse = (res: AttachmentsResponse): CampaignAttachments => ({
    ...(res?.module?.campaignAttachments || {}),
    subCampaignAttachments: res?.module?.subCampaignAttachments,
  });

  const getAttachments = (setLoader = true) => {
    if (setLoader) setIsLoading(true);

    const request = subCR
      ? campaignServices.getSubCampaign(id, 'attachments', version)
      : campaignServices.getAttachments(id);

    request
      .then((res) => setAttachments(subCR ? parseSubCRResponse(res) : res))
      .catch(handleCriticalError)
      .finally(() => { if (setLoader) setIsLoading(false); });
  };

  useEffect(() => { getAttachments(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteFile = (fileId: string) => {
    setDeleteFileId(null);
    const service = subCR
      ? campaignServices.deleteSubCampaignAttachment
      : campaignServices.deleteAttachment;
    service(fileId, id)
      .then(() => deleteAttachment(fileId))
      .catch(handleServiceError);
  };

  const setFavorite = (attachmentId: string) => {
    toggleFavorite(attachmentId);
    campaignServices.setFavorite(attachmentId, id).catch(handleServiceError);
  };

  const updateAttachmentField = (attachmentId: string, field: 'jvVisible' | 'tag', value: string | boolean) => {
    const source = isInSubCampaign(attachmentId) ? afterAttachments.attachments : campaignAttachments.attachments;
    const att = source.find((file) => file.id === attachmentId);
    if (!att) return;

    const isJvChange = field === JV_VISIBLE;
    const newJv = isJvChange ? (value as boolean) : (att.jointVenture ?? false);
    const newTag = isJvChange ? (att.tag ?? att.documentTag ?? '') : (value as string);
    const changes = isJvChange
      ? { jointVenture: newJv }
      : { tag: newTag, documentTag: newTag };

    updateField(attachmentId, changes);

    const service = isInSubCampaign(attachmentId)
      ? campaignServices.updateSubCampaignAttachment
      : campaignServices.updateAttachmentJvVisibility;

    service(attachmentId, id, newJv, newTag).catch(handleServiceError);
  };

  const handleFilesSelected = (files: FileList, section: string) => {
    if (!files?.length) return;
    const sanitized = removePercentageFromName(files) as FileList;

    setPendingUpload({
      files: sanitized,
      section,
      tag: ATTACHMENT_TAGS.PROBLEM_DESCRIPTION,
      jvVisible: false,
    });
  };

  const uploadFiles = async (
    files: FileList, section: string, tag: string,
    jvVisible: boolean, onProgress: (ptc: number) => void,
  ) => {
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append('files', file);

      if (!allExtensionsFile.includes(splitFileName(file.name).extFile)) {
        addToast(`${file.name}: ${t('alert.wrongFileName')}`, 'ERROR');
      }
    });

    const onUploadProgress = (e: ProgressEvent) =>
      onProgress(Math.round((e.loaded * 100) / e.total));
    const uploadUrl = section === ATTACHMENT_SECTION.AFTER_QMB
      ? documentationServices.uploadSubCampaignAttachment
      : documentationServices.uploadAttachment;

    try {
      await uploadUrl(formData, id, onUploadProgress, tag, jvVisible);
    } catch (error: unknown) {
      addToast((error as { message?: string })?.message, 'ERROR');
    }
  };

  const confirmUpload = async () => {
    if (!pendingUpload?.files?.length) {
      setPendingUpload(null);

      return;
    }
    setPendingUpload(null);

    const { files, section, tag, jvVisible } = pendingUpload;
    const uploadedNames = Array.from(files).map((f) => f.name);

    const optimisticFiles = prepareFilesUtil(files, true).map((att) => ({
      ...att, tag, documentTag: tag, section, jointVenture: jvVisible,
    }));

    if (section === ATTACHMENT_SECTION.AFTER_QMB) {
      addAfterQmb(optimisticFiles);
    } else {
      addAttachments(optimisticFiles);
    }

    await uploadFiles(files, section, tag, jvVisible, (progress) => {
      Array.from(files).forEach((file) =>
        setUploadProgress((prev) => ({ ...prev, [file.name]: progress })),
      );
    });

    setTimeout(async () => {
      setUploadProgress(null);
      try {
        const res = subCR
          ? await campaignServices.getSubCampaign(id, 'attachments', version)
          : await campaignServices.getAttachments(id);
        const freshData = subCR ? parseSubCRResponse(res) : res;
        const mergeUploaded = (list: Attachment[]) =>
          (list || []).map((att: Attachment) => {
            if (uploadedNames.some((name) => att.fullName === name || att.id?.endsWith(`/${name}`))) {
              return { ...att, tag: att.tag || tag, documentTag: att.documentTag || tag, jointVenture: jvVisible };
            }

            return att;
          });

        if (section === ATTACHMENT_SECTION.AFTER_QMB && freshData.subCampaignAttachments) {
          setAttachments({
            ...freshData,
            subCampaignAttachments: {
              ...freshData.subCampaignAttachments,
              attachments: mergeUploaded(freshData.subCampaignAttachments.attachments),
            },
          });
        } else {
          setAttachments({ ...freshData, attachments: mergeUploaded(freshData.attachments) });
        }
      } catch { getAttachments(false); }
    }, 1500);
  };

  const prefillCampaignVisible = campaignAttachments?.attachments?.length > 0 && !checkDisabled && userSettings?.cartIA;

  const onPrefillCampaign = () => {
    setGlobalLoading(true);
    new Promise((resolve) => setTimeout(resolve, 1000))
      .then(() => { reloadCR?.(); addToast(t('cartAI.prefilledData'), 'Success'); })
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => setGlobalLoading(false));
  };

  const tagOptions = useMemo(() =>
    Object.values(ATTACHMENT_TAGS).reduce((acc, key) => ({
      ...acc,
      [key]: {
        EN: String(i18n.t(`attachments.tags.${key}`, { lng: 'en' })),
        DE: String(i18n.t(`attachments.tags.${key}`, { lng: 'de' })),
      },
    }), {} as Record<string, { EN: string; DE: string }>),
  []);

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <>
          <AccordionPanel
            title={t('attachments.title')}
            handleClick={() => setHelpArea(HELP_AREA.ATTACHMENTS)}
            additionalContentRight={prefillCampaignVisible && (
              <div className={styles.PrefillCampaignButton} onClick={onPrefillCampaign}>
                {t('cartAI.preFillCampaign')}
              </div>
            )}
          >
            <AttachmentCollection
              fileList={beforeAttachments}
              checkDisable={checkDisabled}
              setFavorite={setFavorite}
              download={(file) => downloadFile(file, file.toString().split('/').pop())}
              setDeleteFileId={setDeleteFileId}
              uploadProgress={uploadProgress}
              onFilesSelected={(files) => handleFilesSelected(files, ATTACHMENT_SECTION.BEFORE_QMB)}
              canDelete={canEditBeforeQmb}
              deleteBlocked={afterAttachments.attachments.length > 0 || !isCreatorOrAdmin}
              dragdropId="attachments-before-dragdrop"
              inputId="addAttachmentBefore"
              onChangeFileTags={updateAttachmentField}
              readOnlyTags={!canEditBeforeQmb}
            />
          </AccordionPanel>

          {subCR && isAfterQMBSection && (
            <AccordionPanel
              title={t('attachments.accompanyingTitle')}
              handleClick={() => setHelpArea(HELP_AREA.ATTACHMENTS)}
            >
              <AttachmentCollection
                fileList={afterAttachments}
                checkDisable={checkDisabled && !canDeleteAfterQmb}
                canEdit={canUpload && isInPlantImplementation}
                canDelete={canDeleteAfterQmb}
                setFavorite={setFavorite}
                download={(file) => downloadFile(file, file.toString().split('/').pop())}
                setDeleteFileId={setDeleteFileId}
                onFilesSelected={(files) => handleFilesSelected(files, ATTACHMENT_SECTION.AFTER_QMB)}
                uploadProgress={uploadProgress}
                dragdropId="attachments-after-dragdrop"
                inputId="addAttachmentAfter"
                onChangeFileTags={updateAttachmentField}
              />
            </AccordionPanel>
          )}

          {pendingUpload && (
            <ConfirmDialog
              close={() => setPendingUpload(null)}
              confirm={confirmUpload}
              title={t('attachments.uploadDialogTitle')}
              closeBtnText={t('dialog.cancel')}
              confirmBtnText={t('attachments.buttonUpload')}
              fullWidth
              maxWidth="xs"
            >
              <div className={styles.UploadDialogFields}>
                <Dropdown
                  id="attachment-tag"
                  label={String(t('attachments.tagLabel'))}
                  value={pendingUpload.tag}
                  options={tagOptions}
                  handleChange={(e) => setPendingUpload({ ...pendingUpload, tag: e.target.value })}
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={pendingUpload.jvVisible}
                      onChange={(e) => setPendingUpload({ ...pendingUpload, jvVisible: e.target.checked })}
                    />
                  )}
                  label={String(t('attachments.jvVisibilityLabel'))}
                />
              </div>
            </ConfirmDialog>
          )}

          {deleteFileId && (
            <ConfirmDialog
              close={() => setDeleteFileId(null)}
              confirm={() => deleteFile(deleteFileId)}
              title={t('dialog.confirmDelete')}
              message={t('dialog.wantToDelete')}
              closeBtnText={t('dialog.no')}
              confirmBtnText={t('dialog.yes')}
            />
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  campaignAttachments: getCampaignAttachments(state),
  campaignInfo: getCampaignInfo(state),
  userSettings: getSettingsInfo(state),
});

const actionCreators = {
  addAttachments: addCampaignAttachments,
  setAttachments: setCampaignAttachments,
  deleteAttachment: deleteCampaignAttachment,
  toggleFavorite: toggleAttachmentFavorite,
  updateField: updateAttachmentFieldAction,
  addAfterQmb: addAfterQmbAttachments,
  setHelpArea: setAreaHelp,
};

export default connect(mapStateToProps, actionCreators)(Attachments);
